import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Check, CheckCheck, Inbox, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNotificationStore } from '@/stores/notification.store';
import { formatRelativeDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { api } from '@/lib/api';
import { eduflowApi } from '@/api/eduflow.api';
import { toast } from '@/components/ui/Toast';

export function NotificationBell() {
  const { t } = useTranslation();
  const {
    notifications,
    unreadCount,
    isDropdownOpen,
    toggleDropdown,
    markAsRead,
    markAllAsRead,
    addNotification,
    removeNotification,
  } = useNotificationStore();
  const user = useAuthStore((s) => s.user);
  const [resolvingUnblockId, setResolvingUnblockId] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleUnblockDecision = useCallback(
    async (requestId: string, approve: boolean, notificationId: string) => {
      setResolvingUnblockId(requestId);
      try {
        if (approve) {
          await eduflowApi.approveUnblockRequest(requestId);
          toast.success(
            t('notifications.unblock.approvedToast', 'Formation débloquée pour cet apprenant.'),
          );
        } else {
          await eduflowApi.rejectUnblockRequest(requestId);
          toast.info(t('notifications.unblock.rejectedToast', 'Demande de déblocage refusée.'));
        }
        removeNotification(notificationId);
      } catch {
        toast.error(
          t('notifications.unblock.resolveError', 'Impossible de traiter la demande. Réessayez.'),
        );
      } finally {
        setResolvingUnblockId(null);
      }
    },
    [removeNotification, t],
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        if (isDropdownOpen) toggleDropdown();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, toggleDropdown]);

  useEffect(() => {
    const role = user?.role;
    if (!role || !['teacher', 'resp_formation', 'institution_admin', 'rh'].includes(role)) return;
    // LyrraOne (formateurs sans établissement) : pas de catalogue institution à sonder.
    if (!user?.institutionId) return;
    const storageKey = `seen_catalog_assignments:${user.id}`;
    const storageInitKey = `${storageKey}:initialized`;

    const pollAssignments = async () => {
      try {
        const raw = await api.get<{
          data?: { catalogItems?: Array<{ id: string; title: string; description?: string | null }> };
          catalogItems?: Array<{ id: string; title: string; description?: string | null }>;
        }>('/institution/content/my-assignments');

        const payload = (raw && typeof raw === 'object' && 'data' in raw && raw.data)
          ? raw.data
          : raw;
        const items = Array.isArray(payload?.catalogItems) ? payload.catalogItems : [];
        const currentIds = new Set(items.map((i) => i.id));
        const seenIds = new Set<string>(
          JSON.parse(localStorage.getItem(storageKey) ?? '[]') as string[],
        );
        const initialized = localStorage.getItem(storageInitKey) === '1';

        if (!initialized) {
          localStorage.setItem(storageInitKey, '1');
          localStorage.setItem(storageKey, JSON.stringify(Array.from(currentIds)));
          return;
        }

        for (const item of items) {
          if (!seenIds.has(item.id)) {
            addNotification({
              type: 'info',
              title: 'Nouvelle mission de production',
              message: item.title,
              link: '/producer-assignments',
            });
            seenIds.add(item.id);
          }
        }

        // Keep only currently assigned ids to avoid infinite growth
        const compacted = Array.from(seenIds).filter((id) => currentIds.has(id));
        localStorage.setItem(storageKey, JSON.stringify(compacted));
      } catch {
        // non bloquant
      }
    };

    void pollAssignments();
    const interval = window.setInterval(() => {
      void pollAssignments();
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [addNotification, user?.id, user?.institutionId, user?.role]);

  // Demandes de déblocage formation (perte de focus) en attente pour le formateur.
  useEffect(() => {
    const role = user?.role;
    if (!role || !['teacher', 'resp_formation', 'institution_admin', 'rh', 'manager', 'DRH'].includes(role)) {
      return;
    }

    const pollUnblockRequests = async () => {
      try {
        const raw = await eduflowApi.listPendingUnblockRequests();
        const payload = (raw as { data?: unknown })?.data ?? raw;
        const items = Array.isArray(payload) ? payload : [];
        for (const item of items) {
          if (!item || typeof item !== 'object') continue;
          const req = item as {
            id: string;
            focusLossCount?: number;
            flow?: { title?: string };
            user?: { firstName?: string; lastName?: string; email?: string };
          };
          if (!req.id) continue;
          const requesterName =
            [req.user?.firstName, req.user?.lastName].filter(Boolean).join(' ').trim() ||
            req.user?.email ||
            t('notifications.unblock.unknownLearner', 'Un apprenant');
          addNotification({
            id: `unblock-req-${req.id}`,
            type: 'warning',
            title: t('notifications.unblock.title', 'Demande de déblocage'),
            message: t('notifications.unblock.message', {
              defaultValue: '{{name}} — {{flow}} ({{count}} sortie(s))',
              name: requesterName,
              flow: req.flow?.title ?? t('notifications.unblock.unknownFlow', 'Formation'),
              count: req.focusLossCount ?? 0,
            }),
            actionType: 'unblock_request',
            actionPayload: {
              requestId: req.id,
              flowTitle: req.flow?.title,
              requesterName,
              focusLossCount: req.focusLossCount,
            },
          });
        }
      } catch {
        /* non bloquant */
      }
    };

    void pollUnblockRequests();
    const interval = window.setInterval(() => {
      void pollUnblockRequests();
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [addNotification, t, user?.id, user?.role]);

  const recentNotifications = notifications.slice(0, 8);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="relative rounded-lg p-2 text-[#5B6472] transition-colors hover:bg-[#F0F4FA] dark:text-[#DCE7F7] dark:hover:bg-[hsl(217,33%,22%)]"
        aria-label={t('notifications.label', 'Notifications')}
        aria-expanded={isDropdownOpen}
        aria-haspopup="true"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -end-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-white dark:ring-[hsl(217,33%,12%)]">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isDropdownOpen && (
        <div className="absolute end-0 mt-2 w-80 sm:w-96 rounded-xl border border-[#E8EDF5] dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#F0F4FA] dark:border-gray-700">
            <h3 className="text-sm font-semibold text-[#0C1C38] dark:text-gray-100">
              {t('notifications.title', 'Notifications')}
              {unreadCount > 0 && (
                <span className="ms-2 inline-flex items-center rounded-full bg-primary-100 dark:bg-primary-900/30 px-2 py-0.5 text-xs font-medium text-primary-700 dark:text-primary-300">
                  {unreadCount}
                </span>
              )}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                {t('notifications.markAllRead', 'Mark all read')}
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-700/50">
            {recentNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <Inbox className="h-10 w-10 text-[#5B6472]/50 dark:text-[#B6CAEC] mb-3" />
                <p className="text-sm text-[#5B6472] dark:text-[#B6CAEC]">
                  {t('notifications.empty', 'No notifications yet')}
                </p>
              </div>
            ) : (
              recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    'flex items-start gap-3 px-4 py-3 hover:bg-[#F8FAFC] dark:hover:bg-gray-700/50 transition-colors cursor-pointer',
                    !notification.read && 'bg-primary-50/50 dark:bg-primary-900/10'
                  )}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && !notification.read && markAsRead(notification.id)}
                >
                  {/* Indicator dot */}
                  <div className="flex-shrink-0 mt-1.5">
                    {!notification.read ? (
                      <div className="h-2.5 w-2.5 rounded-full bg-primary-500" />
                    ) : (
                      <div className="h-2.5 w-2.5 rounded-full bg-transparent" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={cn(
                      'text-sm leading-snug',
                      notification.read
                        ? 'text-[#5B6472] dark:text-[#B6CAEC]'
                        : 'text-[#0C1C38] dark:text-gray-100 font-medium'
                    )}>
                      {notification.title}
                    </p>
                    {notification.message && (
                      <p className="text-xs text-[#5B6472] dark:text-[#B6CAEC] mt-0.5 truncate">
                        {notification.message}
                      </p>
                    )}
                    <p className="text-xs text-[#5B6472]/70 dark:text-[#B6CAEC] mt-1">
                      {formatRelativeDate(new Date(notification.createdAt))}
                    </p>
                    {notification.actionType === 'unblock_request' && notification.actionPayload?.requestId ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          disabled={resolvingUnblockId === notification.actionPayload.requestId}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleUnblockDecision(
                              notification.actionPayload!.requestId,
                              true,
                              notification.id,
                            );
                          }}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {resolvingUnblockId === notification.actionPayload.requestId ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : null}
                          {t('notifications.unblock.approve', 'Débloquer')}
                        </button>
                        <button
                          type="button"
                          disabled={resolvingUnblockId === notification.actionPayload.requestId}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleUnblockDecision(
                              notification.actionPayload!.requestId,
                              false,
                              notification.id,
                            );
                          }}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                        >
                          {t('notifications.unblock.reject', 'Refuser')}
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {!notification.read && notification.actionType !== 'unblock_request' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                      className="flex-shrink-0 p-1 rounded text-[#5B6472]/70 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
                      aria-label={t('notifications.markRead', 'Mark as read')}
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {recentNotifications.length > 0 && (
            <div className="border-t border-[#F0F4FA] dark:border-gray-700 px-4 py-2.5">
              <button
                onClick={() => { toggleDropdown(); window.location.href = '/notifications'; }}
                className="w-full text-center text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                {t('notifications.viewAll', 'View all notifications')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
